// Resolve a targeted (revoked) VSC device to the local PC/SC reader that holds
// it, by identifying each Microsoft Virtual Smart Card reader and matching the
// card serial to the device. Throws vsc:not_on_endpoint when every reader was
// inspected successfully and none matched (a safe no-op — the user must be on
// the endpoint that has it). If a reader failed to identify and nothing
// matched, the underlying bridge/identification error is surfaced instead,
// since absence could not be confirmed.
const VSC_READER_PREFIX = 'Microsoft Virtual Smart Card';

export async function resolveTargetReader(device, deps) {
    const readers = (await deps.listReaders()) || [];
    const vscReaders = readers.filter((r) => r.startsWith(VSC_READER_PREFIX));
    let scanError = null;
    for (let i = 0; i < vscReaders.length; i += 1) {
        const reader = vscReaders[i];
        let card;
        try {
            // eslint-disable-next-line no-await-in-loop
            card = await deps.identify(reader);
        } catch (e) {
            // A per-reader failure (sharing violation, unsupported card,
            // disconnect race, bridge error) must not abort the scan of the
            // remaining readers -- but it does mean we couldn't prove the
            // target is absent from *this* reader, so remember it for the
            // no-match case below.
            scanError = e;
            card = null;
        }
        if (card && card.serial === device.serial) {
            return reader;
        }
    }
    // No reader held the target. If any reader failed to identify, we cannot
    // assert the card is not on this endpoint -- it may be on the reader we
    // couldn't read -- so surface that bridge/identification failure rather
    // than the definitive "not on this computer". Only when every reader was
    // inspected successfully without a match is the card genuinely absent.
    if (scanError) {
        throw scanError;
    }
    const err = new Error('The targeted virtual smart card is not on this computer.');
    err.code = 'vsc:not_on_endpoint';
    throw err;
}
